import * as RadixSelect from '@radix-ui/react-select';
import { ArrowDown, ArrowUp } from '@sds-system/assets';
import type { PolymorphicRef } from '@sds-system/types';
import type { ElementType } from 'react';
import { forwardRef, useState } from 'react';
import { Column } from '../Column';
import { Icon } from '../Icon';
import { Typography } from '../Typography';
import {
  StyledSelectContent,
  StyledSelectItem,
  StyledSelectTrigger,
  selectIconSizeVariant,
} from './Select.styled';
import type { SelectComponent, SelectProps } from './Select.types';

export const Select: SelectComponent = forwardRef(
  <C extends ElementType = 'select'>(
    {
      variant = 'outline',
      options,
      size = 'M',
      width,
      disabled,
      value,
      defaultValue,
      placeholder,
      onValueChange,
      isError,
      errorMessages,
      styledComponents,

      ...props
    }: SelectProps<C>,
    ref: PolymorphicRef<C>,
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
      <Column gap={8} width={width}>
        <RadixSelect.Root
          defaultValue={defaultValue}
          disabled={disabled}
          onOpenChange={(open) => setIsOpen(open)}
          onValueChange={onValueChange}
          open={isOpen}
          value={value}
          {...props}
        >
          <StyledSelectTrigger
            isError={isError}
            onPointerDown={(e) => {
              if (e.pointerType === 'touch') {
                e.preventDefault();
              }
            }}
            onPointerUp={(e) => {
              if (e.pointerType === 'touch') {
                setIsOpen((prev) => !prev);
              }
            }}
            ref={ref}
            size={size}
            variant={variant}
            {...(styledComponents?.trigger && styledComponents.trigger)}
          >
            <RadixSelect.Value placeholder={placeholder} />
            {isOpen ? (
              <Icon size={selectIconSizeVariant[variant]} source={ArrowUp} />
            ) : (
              <Icon size={selectIconSizeVariant[variant]} source={ArrowDown} />
            )}
          </StyledSelectTrigger>
          <RadixSelect.Portal>
            <StyledSelectContent
              position='popper'
              ref={(instance) => {
                if (!instance) return;
                instance.ontouchend = (e) => {
                  e.preventDefault();
                };
              }}
              sideOffset={4}
              variant={variant}
              width={width}
              {...(styledComponents?.content && styledComponents.content)}
            >
              <RadixSelect.Viewport asChild>
                <RadixSelect.Group>
                  {options.map((item) => (
                    <StyledSelectItem
                      key={item.value}
                      size={size}
                      value={item.value}
                      variant={variant}
                      {...(styledComponents?.item && styledComponents.item)}
                    >
                      <RadixSelect.ItemText>{item.label}</RadixSelect.ItemText>
                    </StyledSelectItem>
                  ))}
                </RadixSelect.Group>
              </RadixSelect.Viewport>
            </StyledSelectContent>
          </RadixSelect.Portal>
        </RadixSelect.Root>
        {isError &&
          !isOpen &&
          errorMessages?.map((message) => (
            <Column key={message}>
              <Typography color='text.error' fontType='caption.medium'>
                {message}
              </Typography>
            </Column>
          ))}
      </Column>
    );
  },
);
